import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

export interface IDomainDto {
  id: string;
  domain: string;
  baseDomain?: string;
  hasApexMap: boolean;
  hasWildcardMap: boolean;
  certId?: string;
  status: string;
  authState?: string;
  certState?: string;
  dnsCnameHost?: string;
  dnsCnameTarget?: string;
  done: boolean;
  events?: string[];
  customerTag: string;
}

export interface IStartDomainResponse {
  id: string;
  hostname: string;
  baseDomain: string;
  dnsCnameHost?: string;
  dnsCnameTarget?: string;
  authState: string;
  certId?: string;
  certState: string;
  status: string;
  completed: boolean;
  events: string[];
  customerTag: string;
}

export interface IVerifyDomainResponse {
  ok: boolean;
  success: boolean;
  certId?: string;
  certState: string;
  authState: string;
  mapEntryApex: string;
  mapEntryWildcard: string;
  events: string[];
}

export interface IDomainStatusResponse {
  id: string;
  hostname: string;
  baseDomain: string;
  status: string;
  authState: string;
  certState: string;
  certId?: string;
  dnsCnameHost?: string;
  dnsCnameTarget?: string;
  done: boolean;
  events: string[];
  customerTag: string;
}

export class CloudDomainService {
  /**
   * Get all domains for the current workspace
   */
  public static async GetDomains(): Promise<AxiosResponse<IDomainDto[]>> {
    try {
      const url = `${backendUrl}/clouddomain`;
      return await Axios.get<IDomainDto[]>(url);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /**
   * Get a specific domain by ID
   */
  public static async GetDomainById(id: string): Promise<AxiosResponse<IDomainDto>> {
    try {
      const url = `${backendUrl}/clouddomain/${encodeURIComponent(id)}`;
      return await Axios.get<IDomainDto>(url);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /**
   * Start domain provisioning process
   */
  public static async StartDomain(hostname: string): Promise<AxiosResponse<IStartDomainResponse>> {
    try {
      const url = `${backendUrl}/clouddomain/start`;
      // Disable normalization - send hostname as-is
      // const normalizedHostname = normalizeDomainForWildcard(hostname);
      return await Axios.post<IStartDomainResponse>(url, { hostname });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      // Extract user-friendly error message from API response
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Verify domain DNS configuration
   */
  public static async VerifyDomain(id: string): Promise<AxiosResponse<IVerifyDomainResponse>> {
    try {
      const url = `${backendUrl}/clouddomain/verify`;
      return await Axios.post<IVerifyDomainResponse>(url, { id });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      // Extract user-friendly error message from API response
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Get domain status
   */
  public static async GetDomainStatus(id: string): Promise<AxiosResponse<IDomainStatusResponse>> {
    try {
      const url = `${backendUrl}/clouddomain/${encodeURIComponent(id)}/status`;
      return await Axios.get<IDomainStatusResponse>(url);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /**
   * Delete a domain
   */
  public static async DeleteDomain(id: string): Promise<AxiosResponse<void>> {
    try {
      const url = `${backendUrl}/clouddomain/${encodeURIComponent(id)}`;
      return await Axios.delete<void>(url);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static normalizeDomainForWildcard(input: string): string {
    if (!input || typeof input !== "string") {
      return "";
    }

    let cleanInput = input.trim().toLowerCase();

    if (cleanInput.startsWith("*.")) {
      cleanInput = cleanInput.substring(2);
    }

    if (!cleanInput.match(/^[a-z][a-z0-9+.-]*:\/\//i)) {
      cleanInput = `https://${cleanInput}`;
    }

    try {
      const url = new URL(cleanInput);
      let hostname = url.hostname;

      if (hostname.startsWith("www.")) {
        hostname = hostname.substring(4);
      }

      if (!hostname) {
        return "";
      }

      return `*.${hostname}`;
    } catch (error) {
      return "";
    }
  }
}
